// /anniversaries — list screen (W3.1 §5.1).
//
// Layout (mobile):
//   Z1 PageHero (新增 button)
//   Z2 TodayBanner (conditional)
//   Z3 HeroSection (3 segments, single card)
//   Z4 AnniversaryTimeline (3-section, collapsible future)
//
// W40.1 F5 deleted the category chip rail that used to sit here as Z2 — 7 chips
// under the title to narrow a list nobody narrows, and the one thing it did
// reliably do was hide the 今天 banner by filtering it out. The rail was the
// anniversary page's own filter; the calendar's 「紀念日」 category toggle is a
// different control and is untouched. See ADR-0100's sibling decision recorded
// in the W40.1 spec §0 D9.

import { useCallback, useMemo } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";

import { PageHero } from "@/components/primitives/PageHero";
import { PullToRefreshBar } from "@/components/patterns/PullToRefreshBar";
import { LoadingState } from "@/components/primitives/LoadingState";
import { EmptyState } from "@/components/primitives/EmptyState";
import { ErrorState } from "@/components/primitives/ErrorState";
import { FilterChipGroup } from "@/components/primitives/FilterChipGroup";
import { PageContainer, SectionStack } from "@/components/layout";
import { ApiError } from "@/lib/api/pair";
import { anniversaryTimelineSummary } from "@/lib/heroSummary";
import { HeroSection } from "./components/HeroSection";
import { TodayBanner } from "./components/TodayBanner";
import {
  AnniversaryTimeline,
  type TimelineSectionFilter,
} from "./components/AnniversaryTimeline";
import {
  useAnniversaryTimeline,
  useAnniversaryToday,
  useAnniversariesList,
} from "@/lib/hooks/anniversary";
import { queryKeys } from "@/lib/queries/keys";
import { useQueryClient } from "@tanstack/react-query";

// W40.1 F5 deleted the category chip rail that used to sit here as Z2 — 7 chips
// under the title to narrow a list nobody narrows, and the one thing it did
// reliably do was hide the 今天 banner by filtering it out. The rail was the
// anniversary page's own filter; the calendar's 「紀念日」 category toggle is a
// different control and is untouched. See ADR-0100's sibling decision recorded
// in the W40.1 spec §0 D9.
//
// **W51 (D-4) puts a filter back — a different one.** What is missing is not
// 「by category」 but 「by time」, and the user asked for it in exactly those
// terms: 「紀念日模組應該和日記模組一樣在標題欄下有 filter」. The backend already
// sends `is_past_section` / `is_future_section` and the client already buckets
// three ways, so this is a control over data already in memory: no API change,
// no migration, no refetch.

const SECTION_FILTERS: { id: TimelineSectionFilter; label: string }[] = [
  { id: "all", label: "全部" },
  { id: "past", label: "過去" },
  { id: "now", label: "現在" },
  { id: "future", label: "未來" },
];

const SECTION_PARAM = "section";

/** An unknown or absent value is 「全部」, never a blank page. */
function readSection(raw: string | null): TimelineSectionFilter {
  const match = SECTION_FILTERS.find((f) => f.id === raw);
  return match ? match.id : "all";
}

export function AnniversariesPage() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();

  const section = readSection(params.get(SECTION_PARAM));
  const setSection = useCallback(
    (id: string) => {
      const next = new URLSearchParams(params);
      // 「全部」 is the *absence* of a filter, so it writes no param at all and
      // the default URL stays the clean one.
      if (id === "all") next.delete(SECTION_PARAM);
      else next.set(SECTION_PARAM, id);
      // `replace`, like `Calendar.tsx`: this is a view of the same page, not a
      // new destination, and stacking entries would make Back walk through
      // every chip tap before leaving the screen.
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const chipItems = useMemo(
    () => SECTION_FILTERS.map((f) => ({ id: f.id, label: f.label })),
    [],
  );

  const todayQuery = useAnniversaryToday();
  const listQuery = useAnniversariesList({ limit: 50 });
  // No `category` argument. This is the same call the page already made on
  // load (`category === "all"` sent `undefined`), so the ordering cannot have
  // changed — and `list_anniversaries` puts its `order_by(date, id)` outside
  // the `if category_filter:` branch, so it never depended on the filter
  // anyway. Checked rather than assumed.
  const timelineQuery = useAnniversaryTimeline({
    future_years: 5,
    past_years: 5,
  });

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: queryKeys.anniversary.all });
  };

  const isInitialLoading =
    timelineQuery.isLoading && !timelineQuery.data;

  return (
    <div data-testid="anniversaries-page" className="flex flex-col pb-32">
      <PageHero
        eyebrow="兩個人的時間軸"
        title="紀念日"
        // W52.0 D-1 — the second missing subtitle. Computed from the
        // already-loaded timeline, so it cannot disagree with the timeline and
        // hero card rendered below it. See `@/lib/heroSummary`.
        subtitle={anniversaryTimelineSummary(timelineQuery.data)}
        trailing={
          // W52.0 D-2 — the bin entry is **new here**, not moved. The route was
          // declared in `App.tsx` and asserted by `in-app-link-contract`, and
          // nothing in the app linked to it: an orphan route passes every
          // "is it declared" guard while being unreachable. Soft-deleting an
          // anniversary moved it off this screen with no way back, which is the
          // same defect W39.61 fixed for albums and W39.54 for wishes.
          //
          // Placement + style follow the wishes baseline, so all four bins now
          // sit in the same slot on the title's optical line.
          <div className="flex items-center gap-s-2">
            <Link
              to="/anniversaries/trash"
              data-testid="goto-anniversary-trash"
              aria-label="紀念日垃圾桶"
              className="press shell-btn text-ink-2 hover:bg-black/5 transition-colors duration-quick"
            >
              <Trash2 className="w-4 h-4" aria-hidden />
            </Link>
            <Link
              to="/anniversaries/new"
              aria-label="新增紀念日"
              data-testid="anniversaries-add"
              className="press cta-pill hover:opacity-90 transition-opacity"
            >
              <Plus size={18} aria-hidden />
              新增
            </Link>
          </div>
        }
      />

      <PageContainer vertical="loose">
        <PullToRefreshBar onRefresh={refresh} data-testid="anniversaries-pull-to-refresh">
          <SectionStack density="loose">
            <TodayBanner items={todayQuery.data?.items ?? []} />

            <HeroSection totalCount={listQuery.data?.items.length ?? null} />

            {/* W51 (D-4) — 「標題欄下」 is literal: this sits directly under
                the hero, above the banner, so it reads as a page-level filter
                rather than as a control on one card. `ink-1` reads as a settled
                selection rather than a brand accent (FilterChipGroup's own
                note), and `wrap` because four chips do not overflow. */}
            <FilterChipGroup
              items={chipItems}
              selectedId={section}
              onSelect={setSection}
              accent="ink-1"
              layout="wrap"
              ariaLabel="依時間篩選"
              className="px-s-4"
            />

            {isInitialLoading ? (
              <LoadingState count={6} shape="row" />
            ) : timelineQuery.isError ? (
              <ErrorState
                title="載入紀念日失敗"
                retryLabel="重試"
                onRetry={() => void timelineQuery.refetch()}
              />
            ) : timelineQuery.data &&
              timelineQuery.data.years.length === 0 ? (
              <EmptyState
                halo="gold"
                title="還沒有紀念日"
                description="建立第一個紀念日, 把重要的日子記下來"
                actionLabel="新增第一個紀念日"
                onAction={() => {
                  window.location.href = "/anniversaries/new";
                }}
              />
            ) : timelineQuery.data ? (
              <AnniversaryTimeline data={timelineQuery.data} section={section} />
            ) : null}

            {timelineQuery.isError && timelineQuery.error instanceof ApiError ? (
              <p className="px-4 text-meta text-partner-strong">{timelineQuery.error.message}</p>
            ) : null}
          </SectionStack>
        </PullToRefreshBar>
      </PageContainer>
    </div>
  );
}